import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { ArrowRight, BookOpen, Calendar } from "lucide-react";
import { format } from "date-fns";

export default function BlogIndex() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/blog", { params: { limit: 50 } })
      .then((r) => setPosts(r.data || []))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const formatDate = (iso) => {
    try { return format(new Date(iso), "MMM d, yyyy"); } catch { return ""; }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="blog-index">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5BA83F]">{t("blog_page.eyebrow")}</p>
      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1B3A6B] mt-3 tracking-tight">{t("blog_page.title")}</h1>
      <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">{t("blog_page.intro")}</p>

      {loading && (
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl bg-slate-100 animate-pulse h-72" />
          ))}
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="mt-16 text-center py-16 px-6 bg-slate-50 rounded-2xl ring-1 ring-slate-200" data-testid="blog-empty">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto" />
          <h3 className="font-heading text-xl font-semibold text-[#0F172A] mt-4">{t("blog_page.empty_title")}</h3>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">{t("blog_page.empty_subtitle")}</p>
        </div>
      )}

      {!loading && posts.length > 0 && (
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              className="group flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-[#5BA83F] hover:shadow-lg transition-all"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                {p.cover_image ? (
                  <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1B3A6B] to-[#5BA83F]">
                    <BookOpen className="w-10 h-10 text-white/70" />
                  </div>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                {p.tags && p.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {p.tags.slice(0, 3).map((tag, i) => (
                      <span key={i} className="text-[10px] uppercase tracking-wide font-semibold text-[#3F7A1F] bg-[#E8F5E0] px-2 py-0.5 rounded-full">{tag}</span>
                    ))}
                  </div>
                )}
                <h3 className="font-heading text-lg font-bold text-[#0F172A] leading-snug group-hover:text-[#1B3A6B] transition-colors">{p.title}</h3>
                {p.excerpt && <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>}
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(p.published_at)}</span>
                  <span className="inline-flex items-center gap-1 text-[#5BA83F] font-semibold group-hover:translate-x-0.5 transition-transform">
                    {t("blog_page.read_more")} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
